import React, { useState } from 'react';
import { CommunicationsResponse } from '@/api/types';
import { Send, Mail, CheckCircle2, Loader2, Copy, RefreshCw } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { RichInput } from '@/components/ai-tools/RichInput';

type EmailType = 'Initial Application Follow-up' | 'Post-Interview Thank You' | 'Post-Interview Follow-up';
const EMAIL_TYPES: EmailType[] = ['Initial Application Follow-up', 'Post-Interview Thank You', 'Post-Interview Follow-up'];

interface CommunicationsPanelProps {
  result?: CommunicationsResponse | null;
  onSave: () => void;
  isSaving: boolean;
  isGenerating?: boolean;
  onChange: (res: CommunicationsResponse) => void;
  onRegenerate?: (emailType: EmailType) => void;
  onCancel?: () => void;
}

export const CommunicationsPanel: React.FC<CommunicationsPanelProps> = ({ result, onSave, isSaving, isGenerating, onChange, onRegenerate, onCancel }) => {
  const [activeType, setActiveType] = useState<EmailType>('Initial Application Follow-up');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(result?.markdown || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex flex-col flex-1 bg-white dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
      <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-5 border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 z-10 flex-col md:flex-row gap-4 md:gap-2 rounded-t-xl shrink-0">
        <div className="flex items-center gap-2 text-slate-900 dark:text-white">
          <Send className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
          <h2 className="text-sm font-semibold tracking-tight w-full sm:w-auto text-center sm:text-left">Outreach & Networking</h2>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="w-full sm:w-64">
            <Select
              title="Email Type"
              value={activeType}
              onChange={(val) => setActiveType(val as EmailType)}
              options={EMAIL_TYPES.map(type => ({ value: type, label: type }))}
            />
          </div>
          {onRegenerate && !result?.markdown && (
            <Button
              variant="primary"
              onClick={() => onRegenerate(activeType)}
              className="h-[38px] px-4"
              disabled={isSaving || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </Button>
          )}
          {onRegenerate && !!result?.markdown && (
            <Button
              variant="ghost"
              onClick={() => onRegenerate(activeType)}
              disabled={isSaving || isGenerating}
              className="flex-1 sm:flex-none h-auto py-1.5 px-3 min-h-[36px]"
            >
              <RefreshCw className={`w-4 h-4 shrink-0 ${isGenerating ? 'animate-spin' : ''}`} />
              <span className="whitespace-nowrap ml-1.5 inline">{isGenerating ? 'Generating...' : 'Retry'}</span>
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        {/* Content Area */}
        <div className="h-full overflow-y-auto px-4 pt-4 lg:px-6 lg:pt-6 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
          <div className="max-w-4xl mx-auto w-full flex flex-col relative min-h-full pb-4 lg:pb-6">
            <div className="flex items-center justify-between mb-3 text-emerald-700 dark:text-emerald-400 font-medium shrink-0">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>{activeType} Email</span>
              </div>
              <Button
                variant="primary"
                onClick={handleCopy}
                disabled={isGenerating}
                className="h-auto py-1.5 px-3 min-h-[36px]"
              >
                {copied ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Copy className="w-4 h-4 shrink-0" />}
                <span className="ml-1.5">{copied ? 'Copied!' : 'Copy'}</span>
              </Button>
            </div>
            {isGenerating ? (
              <div className="flex-1 w-full border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/50 flex flex-col items-center justify-center min-h-[300px] shrink-0">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-4" />
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">Drafting your email...</p>
                {onCancel && (
                  <button 
                    onClick={onCancel}
                    className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <RichInput
                className="flex-1 w-full min-h-[300px] shrink-0 bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500/50 p-6 lg:p-8 outline-none text-slate-800 dark:text-slate-200 text-[15px] leading-relaxed transition-all"
                value={result?.markdown || ''}
                onChange={(val) => onChange(result ? { ...result, markdown: val } : { markdown: val } as any)}
                placeholder="Click 'Generate' to create this email..."
              />
            )}
            {/* Safe padding element for scroll distance */}
            <div className="h-4 lg:h-6 shrink-0 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
};
